/** Display formatting shared by views. */
import { CREDIT_ROLES, ENTITY_PATHS, FIELD_LABELS, GAP_LABELS, type EntityType } from '../../shared/model'

const nf = new Intl.NumberFormat()
export const num = (n: number) => nf.format(n)

export const plural = (n: number, one: string, many = `${one}s`) => `${num(n)} ${n === 1 ? one : many}`

/** "adventure" -> "Adventure", "random-roll-tables" -> "Random roll tables". */
export function categoryLabel(slug: string): string {
  const s = slug.replace(/-/g, ' ')
  if (s === 'gm tools') return 'GM tools'
  if (s === 'srd') return 'SRD'
  if (s === 'army book') return 'Army book (codex, battletome)'
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export const fieldLabel = (f: string) => FIELD_LABELS[f] ?? GAP_LABELS[f] ?? f

export const ROLE_LABELS: Record<(typeof CREDIT_ROLES)[number], string> = {
  author: 'Author',
  artist: 'Artist',
  editor: 'Editor',
  designer: 'Designer',
  cartographer: 'Cartographer',
  translator: 'Translator',
  layout: 'Layout',
}

export const TYPE_LABELS: Record<EntityType, { one: string; many: string }> = {
  system: { one: 'System', many: 'Systems' },
  book: { one: 'Book', many: 'Books' },
  series: { one: 'Series', many: 'Series & settings' },
  publisher: { one: 'Publisher', many: 'Publishers' },
  creator: { one: 'Creator', many: 'Creators' },
}

export const recordHref = (type: EntityType, id: string) => `/${ENTITY_PATHS[type]}/${id}`

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31_536_000_000],
  ['month', 2_592_000_000],
  ['week', 604_800_000],
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
]
export function ago(ms: number, now = Date.now()): string {
  const diff = ms - now
  for (const [unit, size] of UNITS) {
    if (Math.abs(diff) >= size) return rtf.format(Math.round(diff / size), unit)
  }
  return 'just now'
}

const dtf = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' })
export const fullDate = (ms: number) => dtf.format(new Date(ms))

const monthYear = new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'long', timeZone: 'UTC' })
const dayMonthYear = new Intl.DateTimeFormat(undefined, { dateStyle: 'long', timeZone: 'UTC' })
/** A publication date at whatever precision is known: "2019", "March 2019", "March 14, 2019". */
export function pubDate(year: number | null, month: number | null, day: number | null): string {
  if (!year) return ''
  if (!month) return String(year)
  const date = new Date(Date.UTC(year, month - 1, day ?? 1))
  return (day ? dayMonthYear : monthYear).format(date)
}

export const LANGUAGE_NAMES = new Intl.DisplayNames(undefined, { type: 'language' })
export function languageName(code: string): string {
  if (!code) return ''
  try {
    return LANGUAGE_NAMES.of(code) ?? code
  } catch {
    return code
  }
}
